import { PropsWithChildren } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { useToast } from '@givebutter/primitives/toast';
import { deleteAccountMember, deleteUserInvite } from 'api/account';
import { checkIfMe } from 'assets/js/pages/account-settings/users/utils';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { accountQueryKeys } from 'queries/account-query-keys';
import { AccountMemberOrInvite } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface DeleteMemberDialogProps extends PropsWithChildren {
    member: AccountMemberOrInvite;
}

const DeleteMemberDialog = (props: DeleteMemberDialogProps) => {
    const toast = useToast();
    const { children, member } = props;
    const queryClient = useQueryClient();
    const { accountId, access } = useCurrentAccount();

    const { mutate: deleteMember, isPending: isDeleting } = useMutation<
        void,
        GivebutterAxiosError,
        { id: number }
    >({
        mutationFn: ({ id }) => deleteAccountMember(accountId, id),
        onSuccess: () => {
            toast.success('Deleted user successfully!');
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.members(accountId),
                type: 'all',
            });
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.membersWithInvitesWithFilters(accountId),
            });
        },
        onError: (e: GivebutterAxiosError) => {
            toast.error(e.response?.data.message ?? 'Failed to delete user');
        },
    });

    const { mutate: deleteInvite, isPending: isDeletingInvite } = useMutation<
        void,
        GivebutterAxiosError,
        { id: string }
    >({
        mutationFn: ({ id }) => deleteUserInvite(accountId, id),
        onSuccess: () => {
            toast.success('Deleted invite successfully!');
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.membersWithInvitesWithFilters(accountId),
            });
        },
        onError: (e: GivebutterAxiosError) => {
            toast.error(e.response?.data.message ?? 'Failed to delete invite');
        },
    });

    const isMe = checkIfMe(member, access);
    const typeName = member.morph_type === 'account_member' ? 'user' : 'user invite';

    return (
        <AlertDialog.Root type={'error'}>
            {children}
            <AlertDialog.Portal>
                <AlertDialog.Overlay />
                <AlertDialog.Content>
                    <AlertDialog.Header>
                        <AlertDialog.Icon />
                        <AlertDialog.Close />
                    </AlertDialog.Header>
                    <AlertDialog.Body>
                        <AlertDialog.Title>Delete {typeName}</AlertDialog.Title>
                        <AlertDialog.Description>
                            You will not be able to undo this action.
                        </AlertDialog.Description>
                    </AlertDialog.Body>
                    <AlertDialog.Footer>
                        <AlertDialog.Cancel>Close</AlertDialog.Cancel>
                        <AlertDialog.Action
                            onClick={() => {
                                if (member.morph_type === 'account_member') {
                                    deleteMember({
                                        id: member.user_id,
                                    });
                                }
                                if (member.morph_type === 'invite') {
                                    deleteInvite({
                                        id: member.invite_id,
                                    });
                                }
                            }}
                            disabled={isDeleting || isDeletingInvite || isMe}
                        >
                            Delete
                        </AlertDialog.Action>
                    </AlertDialog.Footer>
                </AlertDialog.Content>
            </AlertDialog.Portal>
        </AlertDialog.Root>
    );
};

const DeleteMemberDialogTrigger = AlertDialog.Trigger;

const Root = DeleteMemberDialog;
const Trigger = DeleteMemberDialogTrigger;

export type { DeleteMemberDialogProps };

export {
    DeleteMemberDialog,
    DeleteMemberDialogTrigger,
    //
    Root,
    Trigger,
};
